// v10/src/components/admin/service/ServiceTab.js
//
// Pestaña "Servicio por componente" del módulo Mantenimiento.
// Listado por aeronave de componentes con service (Service general, Bujías, ...),
// con horas remanentes, intervalo y botón "Service hecho".
//
// Distinto de "Vida límite por aeronave" (A6.1, TBO): acá se trata del service
// periódico que se descuenta al cerrar cada vuelo y se resetea al hacer
// mantenimiento.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, RefreshCw, AlertCircle, Loader, Wrench, History,
  CheckCircle2, AlertTriangle, X, Edit2, Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/apiService';
import serviceComponentsService from '../../../services/serviceComponentsService';
import { useToast } from '../../../hooks/useToast';
import useIsMobile from '../../../hooks/useIsMobile';
import { POLLING_INTERVALS } from '../../../config/realtime';

const MANAGE_ROLES = new Set(['admin', 'instructor', 'superadmin']);
const DELETE_ROLES = new Set(['admin', 'superadmin']);

const fmtHours = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
};

const statusColor = (status) => {
  if (status === 'overdue') return '#dc2626';
  if (status === 'near')    return '#ea580c';
  return '#16a34a';
};

const statusIcon = (status) => {
  if (status === 'overdue') return AlertTriangle;
  if (status === 'near')    return AlertCircle;
  return CheckCircle2;
};

// ──────────────────────────────────────────────────────────────────
// Modal genérico inline (alta/edición + service hecho + historial + borrar)
// Pattern A6.1: backdrop click + ESC para cerrar.
// ──────────────────────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children, darkMode }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: darkMode ? '#1e293b' : 'white',
          color: darkMode ? 'white' : '#0f172a',
          borderRadius: 18, width: '100%', maxWidth: 520, padding: 24,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{title}</h3>
          <button onClick={onClose} type="button"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', padding: 4 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
};

const inp = (darkMode) => ({
  width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 14,
  background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
  color: darkMode ? 'white' : '#0f172a',
  border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #cbd5e1',
  outline: 'none', boxSizing: 'border-box',
});

const btn = (color, darkMode) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '9px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
  background: color, color: 'white',
});

// Botón secundario con texto legible (resuelve el contraste pobre del gris claro
// sobre texto blanco). Borde definido + texto oscuro en light, claro en dark.
const btnSecondary = (darkMode) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '9px 14px', borderRadius: 10, cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
  background: darkMode ? 'rgba(255,255,255,0.08)' : 'white',
  color:      darkMode ? 'rgba(255,255,255,0.92)' : '#1e293b',
  border:     darkMode ? '1px solid rgba(255,255,255,0.22)' : '1px solid #94a3b8',
});

// ──────────────────────────────────────────────────────────────────
// Formulario Alta / Edición
// ──────────────────────────────────────────────────────────────────
const ComponentForm = ({ initial, onSubmit, onCancel, darkMode, templates }) => {
  const { t } = useTranslation('admin');
  const [name, setName] = useState(initial?.name || '');
  const [interval, setInterval] = useState(initial?.interval_hours ?? '');
  const [remaining, setRemaining] = useState(initial?.hours_remaining ?? '');
  const [threshold, setThreshold] = useState(initial?.alert_threshold ?? 10);
  const [busy, setBusy] = useState(false);

  const applyTemplate = (tpl) => {
    setName(tpl.name);
    setInterval(tpl.interval_hours);
    if (!initial) setRemaining(tpl.interval_hours); // alta: arranca lleno
    setThreshold(tpl.alert_threshold);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const intervalNum  = Number(interval);
    const thresholdNum = Number(threshold);
    if (!(intervalNum > 0))    return;
    if (!(thresholdNum >= 0))  return;
    setBusy(true);
    try {
      await onSubmit({
        name: name.trim(),
        interval_hours:  intervalNum,
        hours_remaining: remaining !== '' ? Number(remaining) : intervalNum,
        alert_threshold: thresholdNum,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!initial && Array.isArray(templates) && templates.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {templates.map((tpl, idx) => (
            <button key={idx} type="button" onClick={() => applyTemplate(tpl)}
              style={{
                ...btn(darkMode ? 'rgba(99,102,241,0.18)' : '#eef2ff', darkMode),
                color: '#6366f1', padding: '6px 10px', fontSize: 12,
              }}>
              {tpl.name} ({tpl.interval_hours}h)
            </button>
          ))}
        </div>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
        {t('service_form_name')}
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
          required style={inp(darkMode)} placeholder={t('service_form_name_ph')} />
      </label>
      <div style={{ display: 'flex', gap: 10 }}>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
          {t('service_form_interval')}
          <input type="number" min="0.1" step="0.1" value={interval}
            onChange={(e) => setInterval(e.target.value)} required style={inp(darkMode)} />
        </label>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
          {t('service_form_threshold')}
          <input type="number" min="0" step="0.1" value={threshold}
            onChange={(e) => setThreshold(e.target.value)} required style={inp(darkMode)} />
        </label>
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
        {t('service_form_remaining')}
        <input type="number" step="0.1" value={remaining}
          onChange={(e) => setRemaining(e.target.value)} style={inp(darkMode)}
          placeholder={t('service_form_remaining_ph')} />
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
        <button type="button" onClick={onCancel} disabled={busy}
          style={btnSecondary(darkMode)}>
          {t('service_action_cancel')}
        </button>
        <button type="submit" disabled={busy}
          style={btn('#6366f1', darkMode)}>
          {busy ? <Loader style={{ width: 14, height: 14 }} /> : null}
          {t('service_action_save')}
        </button>
      </div>
    </form>
  );
};

// ──────────────────────────────────────────────────────────────────
// Formulario "Service hecho"
// ──────────────────────────────────────────────────────────────────
// Devuelve el horómetro actual del avión convertido a string (Postgres lo manda
// como string NUMERIC o como número). null/undefined → ''. Sirve para precargar
// el input del modal "Service hecho".
const aircraftHobbsString = (aircraft) => {
  const h = aircraft?.hobbs_current;
  if (h === null || h === undefined || h === '') return '';
  const n = Number(h);
  return Number.isFinite(n) ? String(n) : '';
};

export const MarkServiceForm = ({ component, aircraft, onSubmit, onCancel, darkMode }) => {
  const { t } = useTranslation('admin');
  // Dos modos:
  //   'done'   → "Service recién hecho": backend resetea a interval_hours.
  //   'adjust' → "Ajustar al estado real": backend fija hours_remaining al
  //              valor custom (permite <= 0 con confirmación explícita → AOG).
  const [mode, setMode] = useState('done');
  const [hobbsAt, setHobbsAt] = useState(() => aircraftHobbsString(aircraft));
  const [note, setNote] = useState('');
  const [customRemaining, setCustomRemaining] = useState('');
  const [aogAck, setAogAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState('');

  const intervalNum  = Number(component?.interval_hours);
  const thresholdNum = Number(component?.alert_threshold);
  const parsedCustom = customRemaining !== '' ? Number(customRemaining) : NaN;
  const isAdjust     = mode === 'adjust';
  const customIsNum  = Number.isFinite(parsedCustom);

  // Estado que quedaría con el valor custom (para el warning en vivo).
  //   overdue → dispararía AOG por A6.2 → requiere confirmación explícita.
  //   near    → no dispara AOG, pero avisa igual.
  //   ok      → sin warning.
  const customStatus = !customIsNum
    ? null
    : parsedCustom <= 0
      ? 'overdue'
      : parsedCustom <= thresholdNum
        ? 'near'
        : 'ok';

  const canSubmit = !busy && (
    !isAdjust ||
    (customIsNum
      && parsedCustom <= intervalNum
      && (customStatus !== 'overdue' || aogAck))
  );

  const submit = async (e) => {
    e.preventDefault();
    setLocalErr('');
    if (isAdjust) {
      if (!customIsNum) {
        setLocalErr(t('service_mark_adjust_err_nan'));
        return;
      }
      if (Number.isFinite(intervalNum) && parsedCustom > intervalNum) {
        setLocalErr(t('service_mark_adjust_err_over_interval', { interval: fmtHours(intervalNum) }));
        return;
      }
      if (customStatus === 'overdue' && !aogAck) {
        setLocalErr(t('service_mark_adjust_err_aog_confirm'));
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        hobbs_at: hobbsAt !== '' ? Number(hobbsAt) : null,
        note: note || null,
      };
      if (isAdjust) payload.hours_remaining = parsedCustom;
      await onSubmit(payload);
    } finally { setBusy(false); }
  };

  const modeBoxStyle = (active) => ({
    display: 'flex', gap: 10, padding: 12, borderRadius: 12, cursor: 'pointer',
    border: active
      ? (darkMode ? '1.5px solid #6366f1' : '1.5px solid #6366f1')
      : (darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0'),
    background: active
      ? (darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(238,242,255,0.55)')
      : 'transparent',
    alignItems: 'flex-start',
  });

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13 }}>
        {t('service_mark_intro_v2', {
          name: component?.name,
          interval: fmtHours(component?.interval_hours),
        })}
      </p>

      {/* Selector de modo — radio-cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={modeBoxStyle(mode === 'done')}>
          <input type="radio" name="markMode" value="done"
            checked={mode === 'done'} onChange={() => setMode('done')}
            style={{ marginTop: 3 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>
              {t('service_mark_mode_done_title')}
            </span>
            <span style={{ fontSize: 12, opacity: 0.75 }}>
              {t('service_mark_mode_done_help', { interval: fmtHours(component?.interval_hours) })}
            </span>
          </div>
        </label>
        <label style={modeBoxStyle(mode === 'adjust')}>
          <input type="radio" name="markMode" value="adjust"
            checked={mode === 'adjust'} onChange={() => setMode('adjust')}
            style={{ marginTop: 3 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>
              {t('service_mark_mode_adjust_title')}
            </span>
            <span style={{ fontSize: 12, opacity: 0.75 }}>
              {t('service_mark_mode_adjust_help')}
            </span>
          </div>
        </label>
      </div>

      {/* Input custom + warning AOG (solo en modo ajuste) */}
      {isAdjust && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
            {t('service_mark_adjust_remaining_label', { interval: fmtHours(component?.interval_hours) })}
            <input type="number" step="0.1" value={customRemaining}
              onChange={(e) => { setCustomRemaining(e.target.value); setAogAck(false); }}
              style={inp(darkMode)} placeholder={t('service_mark_adjust_remaining_ph')} />
          </label>

          {customStatus === 'near' && (
            <div style={{
              padding: '10px 12px', borderRadius: 10, fontSize: 12,
              background: darkMode ? 'rgba(234,88,12,0.12)' : 'rgba(255,237,213,0.7)',
              color: '#ea580c',
              border: darkMode ? '1px solid rgba(234,88,12,0.35)' : '1px solid rgba(234,88,12,0.35)',
            }}>
              <AlertCircle style={{ width: 14, height: 14, verticalAlign: 'text-bottom', marginRight: 6 }} />
              {t('service_mark_adjust_warn_near', { threshold: fmtHours(thresholdNum) })}
            </div>
          )}

          {customStatus === 'overdue' && (
            <div style={{
              padding: '12px 14px', borderRadius: 10, fontSize: 12,
              background: darkMode ? 'rgba(220,38,38,0.14)' : 'rgba(254,226,226,0.85)',
              color: '#b91c1c',
              border: '1px solid rgba(220,38,38,0.45)',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div>
                <AlertTriangle style={{ width: 14, height: 14, verticalAlign: 'text-bottom', marginRight: 6 }} />
                <strong>{t('service_mark_adjust_warn_aog_title')}</strong>
              </div>
              <span>{t('service_mark_adjust_warn_aog_body', { name: component?.name })}</span>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={aogAck}
                  onChange={(e) => setAogAck(e.target.checked)} />
                <span style={{ fontWeight: 700 }}>{t('service_mark_adjust_warn_aog_ack')}</span>
              </label>
            </div>
          )}
        </div>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
        {t('service_mark_hobbs_at')}
        <input type="number" step="0.1" min="0" value={hobbsAt}
          onChange={(e) => setHobbsAt(e.target.value)} style={inp(darkMode)} />
        <span style={{ fontSize: 11, opacity: 0.65 }}>
          {t('service_mark_hobbs_at_help')}
        </span>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>
        {t('service_mark_note')}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} style={inp(darkMode)} />
      </label>

      {localErr && (
        <div style={{
          padding: '8px 10px', borderRadius: 8, fontSize: 12,
          background: darkMode ? 'rgba(220,38,38,0.14)' : '#fee2e2', color: '#b91c1c',
        }}>{localErr}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" onClick={onCancel} disabled={busy}
          style={btnSecondary(darkMode)}>
          {t('service_action_cancel')}
        </button>
        <button type="submit" disabled={!canSubmit}
          style={{ ...btn(isAdjust && customStatus === 'overdue' ? '#dc2626' : '#16a34a', darkMode),
                   opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
          <Wrench style={{ width: 14, height: 14 }} />
          {isAdjust ? t('service_mark_adjust_confirm') : t('service_mark_confirm')}
        </button>
      </div>
    </form>
  );
};

// ──────────────────────────────────────────────────────────────────
// Historial
// ──────────────────────────────────────────────────────────────────
export const HistoryModalBody = ({ component, aircraft, darkMode }) => {
  const { t } = useTranslation('admin');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await serviceComponentsService.getHistory(aircraft.id, component.id);
        setRows(res?.data?.data || []);
      } catch (_) { setRows([]); }
      finally { setLoading(false); }
    })();
  }, [aircraft.id, component.id]);
  if (loading) return <p style={{ margin: 0, fontSize: 13 }}>{t('service_history_loading')}</p>;
  if (!rows.length) return <p style={{ margin: 0, fontSize: 13 }}>{t('service_history_empty')}</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
      {rows.map((r) => (
        <div key={r.id} style={{
          padding: 12, borderRadius: 10,
          border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
          background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
        }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>
            {new Date(r.performed_at).toLocaleString('es-AR')}
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 12, opacity: 0.8 }}>
            {r.performed_by_name || '—'} · {r.hobbs_at != null ? `Hobbs ${fmtHours(r.hobbs_at)}` : ''}
          </p>
          {r.note && <p style={{ margin: '6px 0 0', fontSize: 12 }}>{r.note}</p>}
        </div>
      ))}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────
// Card de componente
// ──────────────────────────────────────────────────────────────────
export const ComponentCard = ({ comp, onMarkService, onEdit, onDelete, onHistory, canManage, canDelete, darkMode, locale }) => {
  const { t } = useTranslation('admin');
  const StatusIcon = statusIcon(comp.status);
  const color = statusColor(comp.status);
  const remaining = Number(comp.hours_remaining);
  const interval  = Number(comp.interval_hours);
  const used      = Math.max(0, interval - remaining);
  const pct       = interval > 0 ? Math.max(0, Math.min(100, (used / interval) * 100)) : 0;

  // Colores de texto que se adaptan al tema (mismo patrón que usa ServiceTab y
  // el resto del módulo). Antes los <p> de la card no fijaban color y heredaban
  // de un ancestro oscuro, lo que dejaba ilegibles los números en modo oscuro.
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.70)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.55)' : '#94a3b8';

  return (
    <div style={{
      padding: 16, borderRadius: 16,
      background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
      color: fgPrimary,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: fgPrimary }}>{comp.name}</p>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: fgMuted }}>
            {t('service_card_interval', { hours: fmtHours(interval, locale) })}
            {' · '}
            {t('service_card_threshold', { hours: fmtHours(comp.alert_threshold, locale) })}
          </p>
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '4px 10px', borderRadius: 999,
          background: `${color}1f`, color, fontSize: 11, fontWeight: 800,
        }}>
          <StatusIcon style={{ width: 12, height: 12 }} />
          {t(`service_status_${comp.status}`)}
        </span>
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: '-0.4px', color: fgPrimary }}>
          {fmtHours(remaining, locale)} h
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 11, color: fgFaint }}>
          {t('service_card_remaining_label')}
        </p>
        <div style={{
          marginTop: 8, height: 6, borderRadius: 999,
          background: darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0', overflow: 'hidden',
        }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {canManage && (
          <button type="button" onClick={onMarkService} style={btn('#16a34a', darkMode)}>
            <Wrench style={{ width: 13, height: 13 }} />
            {t('service_card_btn_done')}
          </button>
        )}
        <button type="button" onClick={onHistory}
          style={btnSecondary(darkMode)}>
          <History style={{ width: 13, height: 13 }} />
          {t('service_card_btn_history')}
        </button>
        {canManage && (
          <button type="button" onClick={onEdit}
            style={btnSecondary(darkMode)}>
            <Edit2 style={{ width: 13, height: 13 }} />
            {t('service_card_btn_edit')}
          </button>
        )}
        {canDelete && (
          <button type="button" onClick={onDelete}
            style={btn(darkMode ? 'rgba(220,38,38,0.18)' : '#fee2e2', darkMode)}>
            <Trash2 style={{ width: 13, height: 13, color: '#dc2626' }} />
            <span style={{ color: '#dc2626' }}>{t('service_card_btn_delete')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────
// Tab principal
// ──────────────────────────────────────────────────────────────────
const ServiceTab = ({ darkMode, userRole, locale = 'es-AR', isActive = true, onDashboardChange }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const isMobile = useIsMobile();
  const canManage = MANAGE_ROLES.has(userRole);
  const canDelete = DELETE_ROLES.has(userRole);

  const [aircraftList, setAircraftList] = useState([]);
  const [selectedId,   setSelectedId]   = useState('');
  const [components,   setComponents]   = useState([]);
  const [loadingAc,    setLoadingAc]    = useState(true);
  const [loadingComps, setLoadingComps] = useState(false);
  const [err,          setErr]          = useState('');
  const [templates,    setTemplates]    = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [marking,    setMarking]    = useState(null);
  const [history,    setHistory]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);

  const pollRef = useRef(null);

  const selectedAircraft = aircraftList.find((a) => a.id === selectedId);

  // ── Carga aeronaves ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAc(true);
      try {
        const r = await api.get('/api/aircraft', { params: { is_active: 'true' } });
        const list = r?.data?.data || [];
        if (cancelled) return;
        setAircraftList(list);
        if (!selectedId && list.length) setSelectedId(list[0].id);
      } catch (_) {
        if (!cancelled) { setErr(t('service_load_aircraft_error')); setAircraftList([]); }
      } finally {
        if (!cancelled) setLoadingAc(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Carga templates sugeridos (una vez) ─────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await serviceComponentsService.getTemplates();
        setTemplates(r?.data?.data || []);
      } catch (_) { setTemplates([]); }
    })();
  }, []);

  // ── Carga componentes del avión seleccionado + polling ──────────
  const loadComponents = useCallback(async () => {
    if (!selectedId) { setComponents([]); return; }
    setLoadingComps(true);
    try {
      const r = await serviceComponentsService.listByAircraft(selectedId);
      setComponents(r?.data?.data || []);
    } catch (_) {
      setComponents([]);
      toast?.error?.(t('service_load_components_error'));
    } finally {
      setLoadingComps(false);
    }
  }, [selectedId, t, toast]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  useEffect(() => {
    if (!isActive) return undefined;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(loadComponents, POLLING_INTERVALS.maintenance);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isActive, loadComponents]);

  // ── Dashboard "Necesitan service" (informa al padre) ───────────
  useEffect(() => {
    if (!onDashboardChange) return;
    (async () => {
      try {
        const r = await serviceComponentsService.getDashboard();
        onDashboardChange(r?.data?.data || null);
      } catch (_) { onDashboardChange(null); }
    })();
  }, [components, onDashboardChange]);

  // ── Handlers de mutación ────────────────────────────────────────
  const handleCreate = async (data) => {
    try {
      await serviceComponentsService.create(selectedId, data);
      toast?.success?.(t('service_create_ok'));
      setShowCreate(false);
      await loadComponents();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || t('service_create_err'));
    }
  };
  const handleEdit = async (data) => {
    try {
      await serviceComponentsService.update(selectedId, editing.id, data);
      toast?.success?.(t('service_update_ok'));
      setEditing(null);
      await loadComponents();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || t('service_update_err'));
    }
  };
  const handleMark = async (data) => {
    try {
      await serviceComponentsService.markServiceDone(selectedId, marking.id, data);
      toast?.success?.(t('service_mark_ok'));
      setMarking(null);
      await loadComponents();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || t('service_mark_err'));
    }
  };
  const handleDelete = async () => {
    try {
      await serviceComponentsService.remove(selectedId, deleting.id);
      toast?.success?.(t('service_delete_ok'));
      setDeleting(null);
      await loadComponents();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || t('service_delete_err'));
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  // Estilos del selector (replicamos el de LifespanTab > ByAircraftView para que
  // se vea idéntico — matrícula + modelo, padding grande, label en mayúsculas).
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const aircraftSelectStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.04)' : 'white',
    color: fgPrimary,
    fontFamily: 'inherit',
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Selector de aeronave + botón refresh + botón nuevo */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto auto',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <div>
          <label
            htmlFor="service-aircraft-select"
            style={{
              display: 'block', fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              color: fgFaint, marginBottom: 6,
            }}
          >
            {t('service_select_label')}
          </label>
          {loadingAc ? (
            <div style={{ ...aircraftSelectStyle, color: fgMuted, cursor: 'default' }}>
              {t('service_loading_aircraft')}
            </div>
          ) : (
            <select
              id="service-aircraft-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={aircraftSelectStyle}
            >
              <option value="">{t('service_select_placeholder')}</option>
              {aircraftList.map((ac) => (
                <option key={ac.id} value={ac.id}>
                  {ac.registration} — {ac.manufacturer ? `${ac.manufacturer} ` : ''}{ac.model_name || ''}
                </option>
              ))}
            </select>
          )}
        </div>
        {isMobile ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: (canManage && selectedId) ? 'auto 1fr' : '1fr',
            gap: 10,
            alignItems: 'end',
          }}>
            <button
              type="button"
              onClick={loadComponents}
              aria-label={t('service_refresh_aria')}
              title={t('service_refresh_aria')}
              style={{
                padding: 11, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                color: fgMuted,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <RefreshCw style={{ width: 16, height: 16 }} />
            </button>
            {canManage && selectedId ? (
              <button type="button" onClick={() => setShowCreate(true)}
                style={{ ...btn('#6366f1', darkMode), justifyContent: 'center', width: '100%' }}>
                <Plus style={{ width: 14, height: 14 }} />
                {t('service_add_btn')}
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={loadComponents}
              aria-label={t('service_refresh_aria')}
              title={t('service_refresh_aria')}
              style={{
                padding: 11, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                color: fgMuted, display: 'flex', alignSelf: 'end',
              }}
            >
              <RefreshCw style={{ width: 16, height: 16 }} />
            </button>
            {canManage && selectedId ? (
              <button type="button" onClick={() => setShowCreate(true)}
                style={{ ...btn('#6366f1', darkMode), alignSelf: 'end' }}>
                <Plus style={{ width: 14, height: 14 }} />
                {t('service_add_btn')}
              </button>
            ) : <span />}
          </>
        )}
      </div>

      {/* Estado: cargando / error / vacío */}
      {err && (
        <div style={{ padding: 12, borderRadius: 10, background: '#fee2e2', color: '#991b1b', fontSize: 13 }}>
          {err}
        </div>
      )}
      {loadingComps ? (
        <div style={{ padding: 32, textAlign: 'center', opacity: 0.7 }}>
          <Loader style={{ width: 22, height: 22 }} />
        </div>
      ) : components.length === 0 ? (
        <div style={{
          padding: 32, borderRadius: 16, textAlign: 'center',
          border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
          background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        }}>
          <Wrench style={{ width: 32, height: 32, color: '#6366f1', opacity: 0.6 }} />
          <p style={{ margin: '12px 0 0', fontSize: 14, fontWeight: 700 }}>
            {t('service_empty_title')}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.7 }}>
            {t('service_empty_desc')}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        }}>
          {components.map((c) => (
            <ComponentCard
              key={c.id} comp={c} locale={locale} darkMode={darkMode}
              canManage={canManage} canDelete={canDelete}
              onMarkService={() => setMarking(c)}
              onEdit={() => setEditing(c)}
              onDelete={() => setDeleting(c)}
              onHistory={() => setHistory(c)}
            />
          ))}
        </div>
      )}

      {/* Modales */}
      <AnimatePresence>
        <Modal open={showCreate} onClose={() => setShowCreate(false)}
          title={t('service_modal_create_title')} darkMode={darkMode}>
          <ComponentForm onSubmit={handleCreate} onCancel={() => setShowCreate(false)}
            darkMode={darkMode} templates={templates} />
        </Modal>
        <Modal open={!!editing} onClose={() => setEditing(null)}
          title={t('service_modal_edit_title')} darkMode={darkMode}>
          {editing && (
            <ComponentForm initial={editing} onSubmit={handleEdit}
              onCancel={() => setEditing(null)} darkMode={darkMode} />
          )}
        </Modal>
        <Modal open={!!marking} onClose={() => setMarking(null)}
          title={t('service_modal_mark_title')} darkMode={darkMode}>
          {marking && (
            <MarkServiceForm component={marking} aircraft={selectedAircraft}
              onSubmit={handleMark} onCancel={() => setMarking(null)} darkMode={darkMode} />
          )}
        </Modal>
        <Modal open={!!history} onClose={() => setHistory(null)}
          title={t('service_modal_history_title', { name: history?.name || '' })} darkMode={darkMode}>
          {history && selectedAircraft && (
            <HistoryModalBody component={history} aircraft={selectedAircraft} darkMode={darkMode} />
          )}
        </Modal>
        <Modal open={!!deleting} onClose={() => setDeleting(null)}
          title={t('service_modal_delete_title')} darkMode={darkMode}>
          {deleting && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ margin: 0, fontSize: 13 }}>
                {t('service_modal_delete_confirm', { name: deleting.name })}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" onClick={() => setDeleting(null)}
                  style={btnSecondary(darkMode)}>
                  {t('service_action_cancel')}
                </button>
                <button type="button" onClick={handleDelete} style={btn('#dc2626', darkMode)}>
                  {t('service_action_delete')}
                </button>
              </div>
            </div>
          )}
        </Modal>
      </AnimatePresence>
    </div>
  );
};

export default ServiceTab;
